import { z } from 'zod';

// Form option arrays — must match backend constants exactly. The
// high-intent detector compares these strings literally, so any drift
// silently disables Calendly. See:
// apps/api/src/modules/onboarding/constants/onboarding.constants.ts
export const INDUSTRY_OPTIONS = [
  'B2B SaaS outbound',
  'Lead gen agencies',
  'Recruiters',
  'D2C',
  'Others',
] as const;

export const COMPANY_SIZE_OPTIONS = [
  '1-10',
  '11-50',
  '51-99',
  '100-499',
  '500+',
] as const;

// Pill ranges for the two volume questions, same shape as COMPANY_SIZE_OPTIONS
// above. The submitted string is the pill label itself — the API's high-intent
// check reads its leading number (onboarding.constants.ts), so a label with no
// leading digit (e.g. "Less than 5") reads as 0 and can never trip high intent.
export const MAILBOX_COUNT_OPTIONS = [
  'Less than 5',
  '5-15',
  '15-50',
  '50-100',
  '100-200',
  '200+',
] as const;

export const MONTHLY_EMAIL_VOLUME_OPTIONS = [
  '100-200',
  '200-500',
  '500-1000',
  '1000-3000',
  '3000+',
] as const;

export const OUTREACH_PLATFORM_OPTIONS = [
  'Saleshandy',
  'Instantly',
  'Smartlead',
  'Apollo',
  'Lemlist',
  'Reply.io',
  'Outreach',
  'Salesloft',
] as const;

// Preset answers for "Where did you hear about us?". There is deliberately no
// "Other" row — the field is a creatable combobox, so typing anything not in
// this list is the "other" path.
export const REFERRAL_SOURCE_OPTIONS = [
  'Google Search',
  'YouTube',
  'LinkedIn',
  'Friend / Colleague Referral',
  'Review Site (G2, Capterra, etc.)',
  'Blog / Article',
  'Agency / Partner Recommendation',
] as const;

// Status payload returned by GET /onboarding/status and POST /onboarding/form.
// `calendlySlotTime` and `completedAt` are serialized as JSON Date strings by
// Nest, so we accept ISO8601 strings on the wire.
export const onboardingStatusSchema = z.object({
  formCompleted: z.boolean(),
  emailConnected: z.boolean(),
  warmupEnabled: z.boolean(),
  isHighIntent: z.boolean(),
  isBusinessDomain: z.boolean(),
  shouldShowCalendar: z.boolean(),
  calendlyUrl: z.string().optional(),
  calendlySlotTime: z.string().optional(),
  completedAt: z.string().optional(),
});

export const onboardingFormSchema = z.object({
  phoneNumber: z.string().min(1).max(20),
  countryCode: z.string().min(1).max(10),
  industry: z.string().min(1).max(100),
  companySize: z.string().min(1).max(50),
  mailboxCount: z.string().min(1).max(50),
  monthlyEmailVolume: z.string().min(1).max(50),
  outreachPlatform: z.string().min(1).max(100),
  referralSource: z.string().min(1).max(100),
  selectedSlot: z.string().optional(),
});

export const availableSlotSchema = z.object({
  startTime: z.string(),
  endTime: z.string(),
  status: z.string(),
  inviteesRemaining: z.number(),
});

export const availableSlotsSchema = z.object({
  slots: z.array(availableSlotSchema),
});

export const calendlyBookingSchema = z.object({
  startTime: z.string().min(1),
  // Best-effort IANA timezone — passed straight through to the Calendly
  // invitee record so the confirmation email shows the viewer's own local
  // time instead of UTC. Optional: omitted when `Intl` is unavailable.
  timezone: z.string().min(1).optional(),
});

export const calendlyBookedResponseSchema = z.object({
  eventUri: z.string(),
});

export type OnboardingStatus = z.infer<typeof onboardingStatusSchema>;
export type OnboardingFormInput = z.infer<typeof onboardingFormSchema>;
export type AvailableSlot = z.infer<typeof availableSlotSchema>;
export type CalendlyBookingInput = z.infer<typeof calendlyBookingSchema>;
export type CalendlyBookedResponse = z.infer<typeof calendlyBookedResponseSchema>;
